import _ from 'lodash';
import _isArray from 'lodash/isArray';
import _keys from 'lodash/keys';
import _pickBy from 'lodash/pickBy';
import { CategoryPotencyRanges, POTENCY_UNITS } from 'shared/constants';
import {
  formatWeightOptions,
  getFormattedPotency,
  getPotencyString,
  getPotencyValues,
  isPotencyProduct,
} from 'shared/helpers/products';
import { hasSaleSpecialForMenuType } from 'shared/helpers/specials';

export { getFormattedPotency };

// Matches the canonical "Total Terpenes" compound name regardless of the whitespace/casing a given
// POS adapter's raw lab-result label uses. Confirmed via production data (ECOMM-13001 follow-up) that
// Armageddon's LeafLogix sync writes the per-option compound as the space-less "TotalTerpenes" (see
// TERPENE_NAMES in Armageddon/lib/menu_integration/adapters/leaflogix.rb), not "Total Terpenes" -- an
// exact-string match against the space-separated form therefore never matches any real product, and
// getOptionTotalTerpenes silently (and permanently) fell back to the stale parent-level aggregate this
// whole feature exists to avoid. Other POS adapters/future data may still use the spaced form (e.g. the
// canonical Postgres Terpene record's name is "Total Terpenes"), so this intentionally tolerates both
// rather than hardcoding either single literal.
const TOTAL_TERPENES_COMPOUND_NAME_PATTERN = /^total\s*terpenes$/i;

/**
 * Looks up the "Total Terpenes" compound recorded against the specific weight/package option the
 * shopper has selected (product.POSMetaData.children[]), rather than the product-wide aggregate
 * (product.totalTerpenes), which is merged across every weight option's batch at menu-sync time
 * and therefore can't reflect a per-weight lab result. See ECOMM-13001.
 *
 * Returns null when the option can't be matched or carries no per-option terpene data, so callers
 * can fall back to the product-level aggregate (e.g. for products/options predating this field).
 *
 * @param {Object} product
 * @param {string} [selectedOptionValue] - product.POSMetaData.children[].option, e.g. "3.5g"
 * @returns {{ value: number, unit: string } | null}
 */
export function getOptionTotalTerpenes(product, selectedOptionValue) {
  if (!selectedOptionValue) {
    return null;
  }

  const children = product?.POSMetaData?.children;
  if (!_.isArray(children)) {
    return null;
  }

  const matchingChild = children.find((child) => child.option === selectedOptionValue);
  const totalTerpenesCompound = matchingChild?.terpenes?.find((compound) =>
    TOTAL_TERPENES_COMPOUND_NAME_PATTERN.test(compound?.name ?? '')
  );
  const values = totalTerpenesCompound?.values;

  if (!_.isArray(values) || values.length === 0) {
    return null;
  }

  return {
    value: _.max(values),
    unit: totalTerpenesCompound.unit || POTENCY_UNITS.PERCENTAGE,
  };
}

export function getPotencyStrings(product, selectedOptionValue) {
  const potencyValues = getPotencyValues(product);
  const potencyStrings = getFormattedPotency(product);
  const showTHC = isPotencyProduct(product) && parseFloat(potencyValues.THC) > 0;
  const showCBD = isPotencyProduct(product) && parseFloat(potencyValues.CBD) > 0;
  const showTAC = isPotencyProduct(product) && parseFloat(potencyValues.TAC) > 0;

  const optionTerpenes = getOptionTotalTerpenes(product, selectedOptionValue);
  const terpsRange = product?.totalTerpenes?.range;
  const rawTerpsValue =
    optionTerpenes?.value ?? (_.isArray(terpsRange) && terpsRange.length > 0 ? _.max(terpsRange) : 0);
  const terpsValue = parseFloat(parseFloat(rawTerpsValue).toFixed(2));
  const terpsUnit = optionTerpenes?.unit || product?.totalTerpenes?.unit || POTENCY_UNITS.PERCENTAGE;
  const showTerps = isPotencyProduct(product) && terpsValue > 0;

  return {
    THC: showTHC ? potencyStrings.THC : null,
    CBD: showCBD ? potencyStrings.CBD : null,
    TAC: showTAC ? potencyStrings.TAC : null,
    TERPS: showTerps ? getPotencyString(terpsValue, terpsUnit) : null,
  };
}

export function rangeIsValid(range, maxRange) {
  if (!range || _.some(range, (value) => !_.inRange(value, maxRange[0], maxRange[1] + 1))) {
    return false;
  }
  return true;
}

export function getPricesForMenuType({ product, menuType, returnSaleSpecialPrices = false }) {
  const { recPrices, medicalPrices, recSpecialPrices, medicalSpecialPrices } = product;
  const isMed = menuType === 'med';
  const prices = isMed ? medicalPrices : recPrices;
  const specialPrices = isMed ? medicalSpecialPrices : recSpecialPrices;

  return returnSaleSpecialPrices ? specialPrices : prices;
}

export function getOptionsQualifyingForOffer({ product, offerId }) {
  const matchingOffer = _.find(
    product.specialData?.bogoSpecials,
    ({ specialId, sourceId }) => specialId === offerId || sourceId === offerId
  );
  return matchingOffer?.qualifyingOptions || [];
}

/**
 * Groups product options with their corresponding prices, taking into account menu type and special offers
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.product - The product object containing options and pricing information
 * @param {string} params.menuType - The type of menu (e.g., 'MEDICAL', 'RECREATIONAL')
 * @param {boolean} params.isKiosk - Whether the menu is being displayed in kiosk mode
 * @param {Object} [params.saleSpecial] - Optional sale special to apply to pricing
 * @param {Object} [params.offerSpecial] - Optional offer special to apply to pricing
 * @param {string | undefined} [params.specialId] - Optional ID of the special to apply
 * @returns {Array<{ value: string, label: string, price: string }>} Array of objects containing all relevant info for each option: : [{ value: '1g', label: '1g', price: '10.00' }, { value: '1/8oz', label: '1/8oz', price: '40.00' }]
 */
export function groupOptionsWithPrices({ product, menuType, isKiosk, saleSpecial, offerSpecial, specialId }) {
  const {
    limitsPerCustomer,
    manualInventory,
    Options: options,
    optionsBelowKioskThreshold,
    optionsBelowThreshold,
    POSMetaData,
  } = product;
  const returnSaleSpecialPrices = _.isUndefined(saleSpecial)
    ? hasSaleSpecialForMenuType({ product, menuType })
    : saleSpecial;

  const prices = getPricesForMenuType({
    product,
    menuType,
    returnSaleSpecialPrices,
  });

  const qualifyingOptions = getOptionsQualifyingForOffer({ product, offerId: specialId });

  return formatWeightOptions({
    isKiosk,
    limitsPerCustomer,
    manualInventory,
    options,
    optionsBelowKioskThreshold,
    optionsBelowThreshold,
    prices,
    weightOnlyLabel: true,
    POSMetaData,
    showQualifyingOptions: !!offerSpecial,
    qualifyingOptions,
  });
}

const parsedProductsWeakMap = new WeakMap();
// this method is evil and should be 100% server side instead
export function parseProduct(product) {
  if (!product) {
    return product;
  }

  const cachedResult = parsedProductsWeakMap.get(product);
  if (cachedResult) {
    return cachedResult;
  }

  // These conversions are needed to support existing shape of product data.
  // Eventually these should be moved to the graphQl layer
  const { effects } = product;
  const parsedProduct = {
    ...product,
    // if object, filter out any key with a falsy value
    effects: _isArray(effects) ? effects : _keys(_pickBy(effects, (effect) => !!effect)),
    description: product.description || product.Description,
    name: product.name || product.Name,
    brand: product.brand || {},
  };

  parsedProductsWeakMap.set(product, parsedProduct);

  return parsedProduct;
}

export function potencyIsDefault(potencyRange = [], category) {
  const defaultRange = _.get(CategoryPotencyRanges, category?.key, []);
  return potencyRange[0] === defaultRange[0] && potencyRange[1] === defaultRange[1];
}

export const potencyWithinRange = (potencyContent, targetRange = []) => {
  if (!Array.isArray(targetRange) || targetRange.length !== 2) {
    return false;
  }

  const [min, max] = targetRange;

  if (!potencyContent) {
    if (min === 0) {
      return true;
    }
    return false;
  }

  // potencyContent is an array of a single value, or a two value range: verify:
  const overMin = potencyContent[0];
  const underMax = potencyContent[1] ?? potencyContent[0];

  return overMin >= min && underMax <= max;
};

export function cartItemId(item) {
  const additionalOption = item.additionalOption ? item.additionalOption : _.head(item.product?.AdditionalOptions);
  return `${item.product.id}-${item.option}${additionalOption ? `-${additionalOption}` : ''}`;
}
